/**
 * Which capability each screen needs, and where each screen sits in the rail.
 *
 * The practice and the client use the SAME application — entity scoping already
 * means a client sees one company, so there is no second interface to build.
 * What differs is which tools appear, and that is declared HERE rather than as
 * role checks sprinkled through the components.
 *
 * Adding a screen means adding a line here. Until you do, it appears for nobody,
 * which is the failure mode you want: a forgotten entry hides a screen rather
 * than leaking a bookkeeper's tool into a client's sidebar.
 *
 * The capability strings come from the SERVER, per entity, so this list cannot
 * drift out of step with what the API will actually allow.
 *
 * IN `lib/` RATHER THAN IN Shell.jsx, WHICH IS WHERE IT USED TO LIVE, and the
 * reason is testability and nothing else. `npm test -w web` is node's own test
 * runner over `src/lib/*.test.js`, and node cannot parse JSX — so for as long as
 * this table sat next to a component, the rule "an unknown view is shown to
 * nobody" and the client_user blind-sheet case below could only ever be checked
 * by eye. Shell.jsx imports both names back and re-exports canView, so nothing
 * that used them had to change.
 */
export const VIEW_CAPABILITY = {
  dashboard:           null,               // everyone; its content follows capabilities
  account:             null,               // your own password

  'trial-balance':     'reports.ledger',
  'income-statement':  'reports.summary',
  'balance-sheet':     'reports.summary',
  'general-ledger':    'reports.ledger',
  // The cash book LISTING, which is a report, not the cash book SCREEN, which
  // is capture — two views, two capabilities, and the pair below is not a
  // duplicate. `reports.ledger` because a listing of a bank account is a
  // working paper exactly as the general ledger it is a slice of is, and the
  // API gates it the same way. A client_admin captures a batch and does not
  // read the bank account back.
  'cash-book-listing': 'reports.ledger',
  // The reconciliation REPORT — the statement a bookkeeper signs — and NOT the
  // reconciliation SCREEN, which is `bank-reconciliation` below. Two views, two
  // capabilities, and the pair is not a duplicate for the same reason the cash
  // book's pair above is not. `reports.ledger`, exactly as the cash book
  // listing beside it: a reconciliation of a bank account is a working paper.
  // Deliberately NOT `reconcile.import` — a client_admin holds that, so it may
  // feed a statement in, and holds neither reports.ledger nor
  // reconcile.approve. Someone who may not read the bank account back may not
  // read the reconciliation of it either.
  'reconciliation-statement': 'reports.ledger',
  vat:                 'reports.ledger',
  debtors:             'reports.summary',
  creditors:           'reports.summary',

  journal:             'capture.journal',
  invoice:             'capture.invoice',
  payment:             'capture.payment',
  slips:               'capture.slip',
  'slip-review':       'slips.review',
  'allocation-rules':  'slips.rules',
  // Same capability as the allocation rules beside it: both are standing rules a
  // practice writes once and the import applies.
  'description-rules': 'slips.rules',
  // Practice admin only, because the API's PUT is `entity.settings`: a prefix is
  // a standing decision about the company that can never be changed once used.
  'reference-prefixes': 'entity.settings',
  documents:           'documents.view',
  contacts:            'contacts.manage',

  // Stock. `items` and `stock-locations` are both `stock.items` because saying
  // what an item IS and saying where it can sit are one job done by one person;
  // `stock-on-hand` is `stock.view`, which read_only holds because a valuation
  // it may read but not break down is a figure it cannot check.
  //
  // ⚠ `stock-take` IS `stock.count` AND NOT `stock.view`, and that is the one
  //   line in this table that looks like a mistake and is not. A `client_user`
  //   — the person who actually walks the shelves — holds stock.count WITHOUT
  //   stock.view, so they see Stock take and nothing else in this module. The
  //   API answers them a sheet with no `expectedQty` key at all, because a
  //   count whose sheet already prints the expected figure is a transcription
  //   exercise. Widening this to stock.view would hide the count screen from
  //   the only people who use it.
  //
  // ⚠ A KNOWN, LATENT MISMATCH, recorded rather than papered over: these two
  //   screens are reached with `stock.items`, but the API gates the item list
  //   (`GET /stock-items`) and the location list (`GET /stock-locations`) on
  //   `stock.view`. A role holding the first without the second would see both
  //   screens and be refused their contents. No role in capabilities.js holds
  //   that combination — every grant of stock.items comes with stock.view — so
  //   this is latent and not live. If a role is ever added that separates them,
  //   these two lines are where it bites, and Items.jsx has a degraded branch
  //   waiting for it while StockLocations.jsx does not.
  // The cash book screen is `cashbook.capture` and NOT `cashbook.post`, for the
  // same reason 'stock-take' is stock.count: the screen is where a batch is
  // TYPED, and the person who types it is the one who needs it in their rail. A
  // holder of cashbook.post reaches the same screen because every role holding
  // post also holds capture; what post buys is the Post button, which the API
  // gates on its own and the screen greys from the capability list the server
  // sends. Gating the screen on cashbook.post would hide the cash book from a
  // client_admin, who holds capture alone and is exactly who captures one.
  'cash-book':         'cashbook.capture',

  // ⚠ `reconcile.import` AND NOT `reconcile.approve`, and it is the same call
  //   as 'cash-book' above and 'stock-take' below: the screen is where a
  //   statement is READ IN and the matcher is run, and the person who does
  //   that is the one who needs it in their rail. A client_admin holds import
  //   alone (capabilities.js:235) — they may feed their own bank statement in,
  //   and may not decide what any of it means, because an approval POSTS: a
  //   slip's suspense leg approved against a statement line writes
  //   Dr 2650 / Cr bank into the general ledger. So they reach this screen and
  //   the API refuses their approval; the screen itself greys the tick and the
  //   buttons from the capability list the server sends, exactly as the cash
  //   book greys Post. Gating the SCREEN on reconcile.approve would hide the
  //   import from the only role that holds import without it.
  'bank-reconciliation': 'reconcile.import',

  items:               'stock.items',
  'stock-on-hand':     'stock.view',
  'stock-take':        'stock.count',
  'stock-adjustments': 'stock.adjust',
  'stock-locations':   'stock.items',

  settings:            'entity.settings',
  chart:               'entity.chart',
  opening:             'entity.opening',
  periods:             'entity.periods',

  'new-entity':        'entity.create',
  people:              null,               // resolved in Shell: practice OR client
  keys:                null,               // resolved in Shell: keys.manage
  // The Code tab. null here only so the slug resolves as a view; WHO sees it is
  // not a capability at all but the server's CODE_TAB_EMAILS allowlist, sent
  // down as `codeTab` on /api/auth/me and checked by the Shell and App. Being
  // an administrator is not a grant, which is why no role holds a capability
  // for it.
  code:                null,
};

export const SECTIONS = [
  ['Statements', [
    ['trial-balance', 'Trial balance'],
    ['income-statement', 'Income statement'],
    ['balance-sheet', 'Balance sheet'],
    ['general-ledger', 'General ledger'],
    // Beside the general ledger, because that is what it is a slice of — and
    // in Statements rather than in Capture, where the cash book SCREEN sits:
    // one is read after the fact, the other is typed into.
    ['cash-book-listing', 'Cash book listing'],
    // The report task 9 promised the reconciliation screen: what the ledger and
    // the bank agree on, and every item they do not. In Statements because it
    // is read after the fact — the deciding is done on the reconciliation
    // screen in Capture, and this prints what those decisions add up to.
    ['reconciliation-statement', 'Bank reconciliation statement'],
    ['vat', 'VAT report'],
    ['debtors', 'Debtors age analysis'],
    ['creditors', 'Creditors age analysis'],
  ]],
  // Stock sits in Capture beside the documents it moves, not in a module of its
  // own: an item is captured the way an invoice is, a count is a capture of
  // what is actually on the shelf, and an adjustment is a capture of what
  // happened to it. A separate "Stock" heading would put four screens behind a
  // fifth click for no gain.
  ['Capture', [
    ['journal', 'Journal entry'],
    ['invoice', 'Tax invoice'],
    ['payment', 'Receipt or payment'],
    // Beside the single payment it replaces for the everyday case: the same
    // money, captured a statement at a time instead of a transaction at a time.
    ['cash-book', 'Cash book'],
    // Beside the cash book, because it is the other half of the same job: one
    // says what we think happened, this one checks it against what the bank
    // says did. In Capture rather than in Statements because a statement is
    // read in and decided here — the reconciliation REPORT a bookkeeper signs
    // is task 9 and belongs with the statements when it arrives.
    ['bank-reconciliation', 'Bank reconciliation'],
    ['slips', 'Slips'],
    ['slip-review', 'Review queue'],
    ['allocation-rules', 'Allocation rules'],
    ['description-rules', 'Description clean-up'],
    ['reference-prefixes', 'Reference prefixes'],
    ['items', 'Items'],
    ['stock-on-hand', 'Stock on hand'],
    ['stock-take', 'Stock take'],
    ['stock-adjustments', 'Adjustments'],
    ['documents', 'Documents'],
    ['contacts', 'Customers & suppliers'],
  ]],
  ['Set up', [
    ['settings', 'Company settings'],
    ['chart', 'Chart of accounts'],
    // Where stock may sit is a standing decision about the company, like the
    // chart and the periods — not something captured day to day.
    ['stock-locations', 'Stock locations'],
    ['opening', 'Opening balances'],
    ['periods', 'Periods'],
  ]],
];

/**
 * Can this entity's capabilities reach this view?
 *
 * AN UNKNOWN VIEW IS SHOWN TO NOBODY. A typo in a view name hides the screen
 * rather than exposing it, which is the direction a mistake here has to fail
 * in. Do not weaken it to `?? null`.
 */
export function canView(view, capabilities = []) {
  // Object.hasOwn, NOT `VIEW_CAPABILITY[view] === undefined`. A plain object
  // reaches Object.prototype, so `canView('toString')` and `canView('valueOf')`
  // find a FUNCTION rather than undefined and skip the guard entirely. They
  // happen to end up false today — a function is never in a capability array —
  // but by accident, not by rule, and "unknown views are shown to nobody" is
  // not a rule that should hold by accident.
  if (!Object.hasOwn(VIEW_CAPABILITY, view)) return false;   // unknown view: show nobody
  const needed = VIEW_CAPABILITY[view];
  if (needed === null) return true;
  return capabilities.includes(needed);
}
